import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import { range, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { schema } from './utils';
import { collectionApis } from '../../../apis/collectionApis';
import { FILE_SIZE, MAX_FILE_NUMBER } from '../../../constants';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import {
  fileTooBig,
  fileNotZip,
  isSolrCloud,
  formatBytes,
  mapBlobFormData,
  sentenceCase,
} from '../../../utils/utils';
import SelectInput from '../../elements/select-input/SelectInput';
import ModalButtons from '../../sections/modal/ModalButtons';
import FileUpload from '../file-upload/FileUpload';

const AddCollectionButton = ({
  collections,
  instanceNodes,
  instanceType,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const { projectId, instanceId } = useParams();
  const { showToast } = useToast();

  const [modal, setModal] = useState(false);
  const [tab, setTab] = useState('0');
  const [files, setFiles] = useState([]);
  const [formatError, setFormatError] = useState(false);
  const [sizeError, setSizeError] = useState(false);

  const isCloud = isSolrCloud(instanceType);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      name: '',
      shards: isCloud ? 2 : '',
      replicationFactor: isCloud ? 2 : '',
    },
    resolver: yupResolver(schema(collections, isCloud)),
    shouldUnregister: true,
  });

  const handleOpenModal = () => setModal(true);
  const handleCloseModal = () => {
    setFiles([]);
    setFormatError(false);
    setSizeError(false);
    setModal(false);
  };
  const handleTabChange = (e, newValue) => setTab(newValue);

  const handleAddCollection = async (data) => {
    const payload = { ...data, file: files[0] };
    try {
      const formData = mapBlobFormData(payload, 'info', {
        formKey: 'file',
        requestKey: 'file',
      });
      await collectionApis.createCollection(projectId, instanceId, formData);

      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('createSuccess', {
            ns: 'notifications',
            text: t('collection', { ns: 'instance' }),
          })
        )
      );
    } catch (error) {
      showToast(
        TOAST_TYPE.ERROR,
        sentenceCase(
          t('createError', {
            ns: 'notifications',
            text: t('collection', { ns: 'instance' }),
          })
        )
      );
    } finally {
      handleCloseModal();
      onRefresh();
    }
  };

  const onDrop = (acceptedFiles) => {
    if (isEmpty(acceptedFiles) || files.length >= MAX_FILE_NUMBER) return;

    setSizeError(fileTooBig(acceptedFiles[0].size, FILE_SIZE.BYTES));
    setFormatError(fileNotZip(acceptedFiles[0].type));

    if (
      !fileTooBig(acceptedFiles[0].size, FILE_SIZE.BYTES) &&
      !fileNotZip(acceptedFiles[0].type)
    ) {
      setSizeError(false);
      setFormatError(false);
      setFiles([...files, ...acceptedFiles]);
    }
  };

  const { getRootProps, open, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    noClick: true,
    noKeyboard: true,
  });

  const removeFile = (fileIndex) => {
    const newFiles = [...files];
    newFiles.splice(newFiles.indexOf(fileIndex), 1);
    setFiles(newFiles);
  };

  const invalidForm = () => {
    if (tab === '0') {
      return !isValid;
    } else if (tab === '1') {
      return !isValid || sizeError || formatError || isEmpty(files);
    }
  };

  const selectOptions = range(1, instanceNodes + 1).map((option) => ({
    label: option,
    value: option,
  }));

  return (
    <>
      <Button variant="contained" onClick={handleOpenModal}>
        <AddIcon />
        {t('add', {
          ns: 'buttons',
          text: t('collection', { ns: 'instance' }),
        })}
      </Button>

      <Dialog open={modal} fullWidth>
        <Tabs value={tab} onChange={handleTabChange} variant="fullWidth">
          <Tab label={t('createNew', { ns: 'instance' })} value="0" />
          <Tab label={t('createByImport', { ns: 'instance' })} value="1" />
        </Tabs>

        <DialogTitle>
          {t('addition', {
            ns: 'project',
            text: t('collection', { ns: 'instance' }),
          })}
        </DialogTitle>

        <form onSubmit={handleSubmit(handleAddCollection)}>
          <DialogContent sx={{ pt: 0 }}>
            <Box role="tabpanel" hidden={tab !== '0'} />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                px: 1,
                py: 1,
              }}
            >
              <TextField
                {...register('name')}
                id="name"
                fullWidth
                autoComplete="off"
                error={!!errors?.name}
                helperText={errors?.name?.message}
                label={t('collectionName', { ns: 'fields' })}
                defaultValue=""
                size="small"
              />

              {isCloud && (
                <>
                  <SelectInput
                    controlled
                    control={control}
                    name="shards"
                    label={t('shards', { ns: 'fields' })}
                    options={selectOptions}
                    defaultValue={isCloud ? 2 : ''}
                    labelKey="label"
                    valueKey="value"
                  />
                  <SelectInput
                    controlled
                    control={control}
                    name="replicationFactor"
                    label={t('replicationFactor', { ns: 'fields' })}
                    options={selectOptions}
                    defaultValue={isCloud ? 2 : ''}
                    labelKey="label"
                    valueKey="value"
                  />
                </>
              )}
            </Box>
            <Box role="tabpanel" hidden={tab !== '1'}>
              <div {...getRootProps({ className: 'dropzone' })}>
                <FileUpload
                  files={files}
                  open={open}
                  handleRemove={removeFile}
                  formatError={formatError}
                  sizeError={sizeError}
                  sizeLimit={formatBytes(FILE_SIZE.BYTES, 0)}
                  helperText={t('dropOneZipFile', { ns: 'instance' })}
                  fileErrorMsg={t('onlyZipFile', { ns: 'descriptions' })}
                />
                <input {...getInputProps()} aria-label="upload file" />
              </div>
            </Box>
          </DialogContent>
          <ModalButtons
            onCancel={handleCloseModal}
            onAction={handleSubmit(handleAddCollection)}
            cancelProps={{ type: 'reset' }}
            actionProps={{ disabled: invalidForm(), type: 'submit' }}
            actionText={t('save', { ns: 'buttons' })}
          />
        </form>
      </Dialog>
    </>
  );
};

AddCollectionButton.propTypes = {
  collections: PropTypes.array,
  instanceType: PropTypes.string,
  instanceNodes: PropTypes.number,
  setLoading: PropTypes.bool,
  onRefresh: PropTypes.func.isRequired,
};

export default AddCollectionButton;
